import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { VisualizationStep } from '@code-arcade/engine'

type GameStatus = 'idle' | 'playing' | 'paused' | 'completed' | 'failed'

interface GameState {
  currentAlgorithmId: string | null
  status: GameStatus
  score: number
  elapsedMs: number
  stepIndex: number
  steps: VisualizationStep<unknown>[]
  animationSpeedMs: number
  raceStartedAt: number | null
  raceCompletedAt: number | null

  startGame: (algorithmId: string) => void
  pauseGame: () => void
  resumeGame: () => void
  completeGame: (finalScore: number) => void
  resetGame: () => void
  incrementStep: () => void
  setStepIndex: (index: number) => void
  setSteps: (steps: VisualizationStep<unknown>[]) => void
  addScore: (points: number) => void
  startRace: () => void
  finishRace: () => void
  setAnimationSpeed: (ms: number) => void
}

const initialState = {
  currentAlgorithmId: null as string | null,
  status: 'idle' as GameStatus,
  score: 0,
  elapsedMs: 0,
  stepIndex: 0,
  steps: [] as VisualizationStep<unknown>[],
  animationSpeedMs: 400,
  raceStartedAt: null as number | null,
  raceCompletedAt: null as number | null,
}

export const useGameStore = create<GameState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      startGame: (algorithmId) =>
        set({
          currentAlgorithmId: algorithmId,
          status: 'idle',
          score: 0,
          stepIndex: 0,
          elapsedMs: 0,
          raceStartedAt: null,
          raceCompletedAt: null,
        }),

      pauseGame: () => set({ status: 'paused' }),
      resumeGame: () => set({ status: 'playing' }),

      completeGame: (finalScore) => set({ status: 'completed', score: finalScore }),

      resetGame: () => set(initialState),

      incrementStep: () =>
        set((s) => ({ stepIndex: Math.min(s.stepIndex + 1, s.steps.length - 1) })),

      setStepIndex: (index) =>
        set((s) => ({ stepIndex: Math.max(0, Math.min(index, s.steps.length - 1)) })),

      setSteps: (steps) => set({ steps, stepIndex: 0 }),

      addScore: (points) => set((s) => ({ score: s.score + points })),

      startRace: () => {
        const now = Date.now()
        set({ status: 'playing', raceStartedAt: now, raceCompletedAt: null })
      },

      finishRace: () => {
        const now = Date.now()
        const { raceStartedAt } = get()
        const elapsedMs = raceStartedAt ? now - raceStartedAt : 0
        set({ status: 'completed', raceCompletedAt: now, elapsedMs })
      },

      setAnimationSpeed: (ms) => set({ animationSpeedMs: ms }),
    }),
    { name: 'game-store' }
  )
)
